/**
 * 纯 JavaScript 实现的 SHA-256 哈希函数
 * 用于在 HTTP + IP 环境下替代 crypto.subtle.digest
 * 因为 crypto.subtle 只在 HTTPS 或 localhost 环境下可用
 */

function sha256(str: string): string {
  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
             0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const k = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
             0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
             0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
             0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
             0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
             0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
             0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
             0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
             0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
             0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
             0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
             0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
             0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
             0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
             0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
             0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2];

  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const len = data.length;
  
  // 计算填充后的长度
  const totalLen = ((len + 8 >> 6) + 1 << 6);
  const padded = new Uint8Array(totalLen);
  padded.set(data);
  padded[len] = 0x80;
  
  // 写入原始比特长度（大端序，64位）
  const bits = len * 8;
  const view = new DataView(padded.buffer);
  // 64 位比特长度必须按大端写入：高 32 位在前(offset -8)，低 32 位在后(offset -4)
  // ⚠️ 曾把两者写反，导致除空串外所有哈希都不正确（LAN 登录失败的根因）
  view.setUint32(totalLen - 8, Math.floor(bits / 4294967296), false); // 高32位
  view.setUint32(totalLen - 4, bits >>> 0, false);                    // 低32位

  // 处理每个 512-bit 块
  for (let i = 0; i < totalLen; i += 64) {
    const w = new Uint32Array(64);
    for (let j = 0; j < 16; j++) {
      w[j] = view.getUint32(i + j * 4, false);
    }
    for (let j = 16; j < 64; j++) {
      const s0 = (w[j-15] >>> 7 | w[j-15] << 25) ^ (w[j-15] >>> 18 | w[j-15] << 14) ^ (w[j-15] >>> 3);
      const s1 = (w[j-2] >>> 17 | w[j-2] << 15) ^ (w[j-2] >>> 19 | w[j-2] << 13) ^ (w[j-2] >>> 10);
      w[j] = (w[j-16] + s0 + w[j-7] + s1) | 0;
    }

    let a = h[0], b = h[1], c = h[2], d = h[3],
        e = h[4], f = h[5], g = h[6], hh = h[7];

    for (let j = 0; j < 64; j++) {
      const S1 = (e >>> 6 | e << 26) ^ (e >>> 11 | e << 21) ^ (e >>> 25 | e << 7);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + k[j] + w[j]) | 0;
      const S0 = (a >>> 2 | a << 30) ^ (a >>> 13 | a << 19) ^ (a >>> 22 | a << 10);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;

      hh = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }

    h[0] = (h[0] + a) | 0;
    h[1] = (h[1] + b) | 0;
    h[2] = (h[2] + c) | 0;
    h[3] = (h[3] + d) | 0;
    h[4] = (h[4] + e) | 0;
    h[5] = (h[5] + f) | 0;
    h[6] = (h[6] + g) | 0;
    h[7] = (h[7] + hh) | 0;
  }

  // 转换为十六进制字符串
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += ('00000000' + (h[i] >>> 0).toString(16)).slice(-8);
  }
  return result;
}

// 主哈希函数：优先使用 Web Crypto API，不可用时回退到纯 JS 实现
export async function hashPassword(password: string): Promise<string> {
  // 尝试使用 Web Crypto API（HTTPS 或 localhost 环境）
  if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Web Crypto API 失败，回退到纯 JS 实现
    }
  }

  // 回退到纯 JS 实现（HTTP + IP 环境）
  return sha256(password);
}
